import { toast } from '@klear/ui/hooks/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import isError from 'lodash/isError'
import isFunction from 'lodash/isFunction'

export type MutationErrorLike = { message: string }

interface ToastConfig {
  successTitle?: string | null
  successDescription?: string | ((data?: unknown) => string) | null
  errorTitle?: string | null
  errorDescription?: string | ((error: MutationErrorLike) => string) | null
}

const operationPastTenses = {
  get: 'loaded',
  list: 'listed',
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  restore: 'restored',
  verify: 'verified',
  refresh: 'refreshed',
  void: '',
  send: 'sent',
  accept: 'accepted'
}

export function createToastConfig<T = unknown>(
  entityName: string | null,
  operation?: keyof typeof operationPastTenses,
  getNameFn?: (data: T) => string
): ToastConfig {
  if (entityName === null) {
    return {
      successTitle: null,
      successDescription: null,
      errorTitle: null,
      errorDescription: null
    }
  }

  const operationText = operationPastTenses[operation as keyof typeof operationPastTenses]

  return {
    successTitle: `${entityName}${operationText ? ` ${operationText} ` : ''} successfully`,
    successDescription: getNameFn
      ? (data?: unknown) => `${entityName} "${getNameFn(data as T)}" has been ${operationText}`
      : `${entityName} has been ${operationText}`,
    errorTitle: operation === 'void' ? `${entityName} failed` : `Error: ${operation} ${entityName.toLowerCase()}`,
    errorDescription:
      operation === 'void'
        ? `Failed to ${entityName.toLowerCase()}`
        : `Failed to ${operation} ${entityName.toLowerCase()}`
  }
}

export function createSuccessCallback(config: ToastConfig) {
  return (data?: unknown) => {
    if (config.successTitle) {
      const description = isFunction(config.successDescription)
        ? config.successDescription(data)
        : config.successDescription || 'Operation completed successfully'

      toast({
        title: config.successTitle,
        description,
        variant: 'default'
      })
    }
  }
}

function mutationErrorMessage(error: unknown, config: ToastConfig): string {
  if (isError(error) && error.message) return error.message
  if (isFunction(config.errorDescription)) return config.errorDescription(error as MutationErrorLike)
  return config.errorDescription || 'An error occurred'
}

export function createErrorCallback(config: ToastConfig) {
  return (error: unknown) => {
    if (config.errorTitle) {
      toast({
        title: config.errorTitle || 'Error',
        description: mutationErrorMessage(error, config),
        variant: 'destructive'
      })
    }
  }
}

type MutationHookOptions = {
  onSuccess?: (data: unknown) => void
  onError?: (error: unknown) => void
}

export function useMutationWithToast<TMutation extends (options?: MutationHookOptions) => unknown>(
  useMutation: TMutation,
  toastConfig: ToastConfig,
  additionalOptions?: {
    onSuccess?: (data: unknown) => void
    onError?: (error: MutationErrorLike) => void
    invalidateQueries?: string[]
  }
): ReturnType<TMutation> {
  const queryClient = useQueryClient()

  return useMutation({
    onSuccess: (data: unknown) => {
      createSuccessCallback(toastConfig)(data)

      if (additionalOptions?.invalidateQueries) {
        additionalOptions.invalidateQueries.forEach((queryKey) => {
          const keyParts = queryKey.split('.').filter(Boolean)
          queryClient.invalidateQueries({ queryKey: [keyParts] })
        })
      }

      if (additionalOptions?.onSuccess) {
        additionalOptions.onSuccess(data)
      }
    },
    onError: (error: unknown) => {
      createErrorCallback(toastConfig)(error)

      if (additionalOptions?.onError) {
        additionalOptions.onError(error as MutationErrorLike)
      }
    }
  }) as ReturnType<TMutation>
}
